'use client'

import { isNil, isNull, isUndefined } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { WeeklyCalendar } from '@/entities/contract'
import {
  cn,
  convertToStringDateRange,
  DateRange,
  oneYearLater,
  today,
  useIsTablet,
  type StringDateRange,
} from '@/shared/lib'
import { Button, Calendar, Dialog, Drawer, Separator } from '@/shared/ui'

type Props = {
  children: React.ReactNode
  onChange?: (dateRange?: StringDateRange) => void
  showSearchWithoutDate?: boolean
  onSearchWithoutDate?: () => void
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export const MobilePeriodPicker = ({
  children,
  onChange,
  showSearchWithoutDate,
  onSearchWithoutDate,
  className,
  open: openProp,
  onOpenChange: onOpenChangeProp,
}: Props) => {
  const t = useTranslations('date_selection')
  const isTablet = useIsTablet()

  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState<'calendar' | 'weekly'>('calendar')

  const isControlled = !isUndefined(openProp)
  const resolvedOpen = isControlled ? openProp : isOpen

  const [dateRange, setDateRange] = useState<DateRange>({
    from: today,
    to: undefined,
  })

  const getStartDate = () => dateRange.from as Date
  const getEndDate = () => dateRange.to

  const resetState = () => {
    setStep('calendar')

    setTimeout(() => {
      setDateRange({ from: today, to: undefined })
    }, 200)
  }

  const handleCalendarSelect = (date?: Date) => {
    setDateRange({ from: isNil(date) ? today : date, to: undefined })
    setStep('weekly')
  }

  const handlePeriodSelect = (date: Date) => {
    const range = { from: getStartDate(), to: date }

    const stringRange = convertToStringDateRange({
      dateRange: range,
      format: 'yyyy-MM-dd',
    })
    onChange?.(stringRange)
    handleOpenChange(false)
  }

  const handleBack = () => {
    setStep('calendar')
  }

  const handleSearchWithoutDate = () => {
    onSearchWithoutDate?.()
    handleOpenChange(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (!isControlled) {
      setIsOpen(open)
    }
    onOpenChangeProp?.(open)

    if (!open) resetState()
  }

  const handleTabletCalendarSelect = (date?: Date) => {
    setDateRange({ from: isNil(date) ? today : date, to: undefined })
  }

  if (isNull(isTablet)) {
    return (
      <div className={cn('block w-full min-w-0', className)}>{children}</div>
    )
  }

  if (isTablet) {
    return (
      <Dialog.Root open={resolvedOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger
          asChild
          className={cn('block w-full min-w-0', className)}
        >
          <div>{children}</div>
        </Dialog.Trigger>
        <Dialog.Content
          closeButton
          className="top-auto bottom-0 flex max-h-[90dvh] translate-y-0 flex-col gap-0 rounded-b-none p-0"
        >
          <Dialog.Title className="shrink-0 px-6 pt-6 pb-6">
            {t('move_in_date.title')}
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            {t('move_in_date.title')}
          </Dialog.Description>
          <div className="overflow-y-auto">
            <Calendar
              mode="single"
              size="large"
              required
              className="px-4 pt-0 pb-6 shadow-none"
              classNames={{
                weekdays: 'grid grid-cols-7 gap-x-[9px] mb-3',
                weekday:
                  'h-fit w-full text-center typo-body1 font-medium text-gray-400 [&[aria-label="Sunday"]]:text-red-500 [&[aria-label="일요일"]]:text-red-500',
                day: 'w-full flex justify-center [&[data-selected]]:bg-transparent',
              }}
              selected={getStartDate()}
              onSelect={handleTabletCalendarSelect}
              today={today}
              disabled={{ before: today, after: oneYearLater }}
              startMonth={today}
              endMonth={oneYearLater}
            />
            <Separator size="large" color="light-gray" decorative />
            <WeeklyCalendar
              startDate={getStartDate()}
              endDate={getEndDate()}
              onSelect={handlePeriodSelect}
              showCloseButton={false}
              className={cn(
                'w-full',
                showSearchWithoutDate && '[&>div:last-child]:pb-0',
              )}
            />
          </div>
          {showSearchWithoutDate && (
            <Dialog.Footer className="shrink-0 px-4 py-6">
              <Button
                size="large"
                color="primary"
                fullWidth
                onClick={handleSearchWithoutDate}
              >
                {t('button.search_without_date')}
              </Button>
            </Dialog.Footer>
          )}
        </Dialog.Content>
      </Dialog.Root>
    )
  }

  return (
    <Drawer.Root open={resolvedOpen} onOpenChange={handleOpenChange}>
      <Drawer.Trigger asChild className={cn('block w-full min-w-0', className)}>
        <div>{children}</div>
      </Drawer.Trigger>
      <Drawer.Content className="p-0">
        {step === 'calendar' ? (
          <>
            <Drawer.Title className="px-4 pb-2 font-semibold">
              {t('move_in_date.title')}
            </Drawer.Title>
            <Drawer.Description className="sr-only">
              {t('move_in_date.title')}
            </Drawer.Description>
            <Calendar
              mode="single"
              size="large"
              required
              className="p-4 pb-6 shadow-none"
              classNames={{
                weekdays: 'grid grid-cols-7 gap-x-[9px] mb-3',
                weekday:
                  'h-fit w-full text-center typo-body1 font-medium text-gray-400 [&[aria-label="Sunday"]]:text-red-500 [&[aria-label="일요일"]]:text-red-500',
                day: 'w-full flex justify-center [&[data-selected]]:bg-transparent',
              }}
              selected={getStartDate()}
              onSelect={handleCalendarSelect}
              today={today}
              disabled={{ before: today, after: oneYearLater }}
              startMonth={today}
              endMonth={oneYearLater}
            />
            {showSearchWithoutDate && (
              <div className="px-4 pb-4">
                <Button
                  size="large"
                  color="primary"
                  fullWidth
                  onClick={handleSearchWithoutDate}
                >
                  {t('button.search_without_date')}
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <Drawer.Title className="sr-only">
              {t('duration.title')}
            </Drawer.Title>
            <Drawer.Description className="sr-only">
              {t('duration.title')}
            </Drawer.Description>
            <WeeklyCalendar
              startDate={getStartDate()}
              endDate={getEndDate()}
              onSelect={handlePeriodSelect}
              showCloseButton={false}
              className="w-full [&>div:first-child]:mt-0"
            />
            <div className="px-4 pb-4">
              <Button
                size="large"
                color="secondary"
                fullWidth
                onClick={handleBack}
              >
                {t('button.go_back')}
              </Button>
            </div>
          </>
        )}
      </Drawer.Content>
    </Drawer.Root>
  )
}
